import {
  MyButton,
  MyChip,
  MyConsentStatusChip,
  MyContextMenu,
  MyPopper,
  MyHorizontalTabV2,
  MyStackedModalSlider,
  myToaster,
} from '@interstellar-component'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ChevronDown,
  Edit01,
  File06,
  FilterLines,
  LinkBroken02,
  Repeat04,
  Zap,
} from '@untitled-ui/icons-react'
import PropTypes from 'prop-types'
import { useApp } from '@src/AppContext'
import AuthImage from '@src/components/AuthImage'
import TruncatedText from '@src/components/TruncatedText'
import { useEmployeeDetailsSheet } from './Context'
import { useWorkforce } from '../../Context'
import ReportEnquiryService from '../../../ReportEnquiry/service'
import EditEmployeeSlider from './EditEmployeeSlider'
import RevokeReportForm from './RevokeReportForm'
import EnquirySwitcher from './EnquirySwitcher'

function formatDate(value) {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

export default function ProfileHeader({ employee }) {
  const { currentTabs, setCurrentTabs, canViewReport } = useEmployeeDetailsSheet()
  const { fetchWorkforceDetail, getWorkforce } = useWorkforce()
  const { hasPermission } = useApp()
  // Workforce-context whitelist (mirrors BE in
  // backoffice-service/modules/report_enquiry/report_enquiry.routes.js).
  // Edit, Refresh report, and Revoke consent are all governed by
  // `workforce.edit` even though Refresh + Revoke hit /report-enquiry/... —
  // this keeps the Action menu coherent with role admin: a role that can
  // edit a workforce can also refresh/revoke it.
  const canEditWorkforce = hasPermission('workforce', 'edit')
  const [searchParams] = useSearchParams()
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const tabs = [
    canViewReport && { label: 'Report', value: 'report' },
    { label: 'Personal information', value: 'personal_information' },
  ].filter(Boolean)

  // After Refresh / Revoke: refetch the report, or Personal Information when
  // the user can't see the report (the BE would 403 it).
  const refetchDetail = () => {
    if (!canViewReport) return fetchWorkforceDetail(employee.id, 'personal_information')
    return fetchWorkforceDetail(employee.id, 'report', searchParams.get('enquiry') || null)
  }

  // Refresh report → PATCH /v1/report-enquiry/:id/refresh. BE branches:
  //   - workforce has cb_subject_code   → ME endpoint (no consent / KYC re-capture)
  //   - consent_expiry still in future  → NAE, re-using last KYC row
  //   - else                             → status=awaiting_form + new invitation email
  // Returns updated enquiry detail; we toast + refetch the report tab so the
  // header badges / Report content reflect the new state.
  const resolveLatestEnquiryId = () => {
    const fromUrl = searchParams.get('enquiry')
    if (fromUrl) return fromUrl
    const history = Array.isArray(employee?.enquiry_history) ? employee.enquiry_history : []
    return history[0]?.id ?? null
  }

  const handleRefreshReport = async (close) => {
    close()
    if (isRefreshing) return
    const enquiryId = resolveLatestEnquiryId()
    if (!enquiryId) {
      myToaster({ message: 'No enquiry on this workforce to refresh yet.', type: 'error' })
      return
    }
    setIsRefreshing(true)
    try {
      const res = await ReportEnquiryService.refresh(enquiryId)
      myToaster(res)
      // Refetch the active tab plus the list. Report is the slow one (CLIK
      // outcome lands here once the dispatcher finishes); the list keeps
      // the row badge fresh.
      await Promise.all([refetchDetail(), getWorkforce()])
    } catch (err) {
      myToaster(err)
    } finally {
      setIsRefreshing(false)
    }
  }

  const consentStatus = employee.consent_status ?? employee.consentStatus
  const updatedAt = employee.updated_at ?? employee.updatedAt
  const updatedLabel = formatDate(updatedAt)
  const code = employee.workforce_code ?? employee.code ?? employee.employeeId

  return (
    <div className="flex flex-col gap-6 pb-8 border-b border-gray-200 bg-white">
      {/* Main Header Content */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          {/* Avatar */}
          {employee.avatar_url ?? employee.avatar ? (
            <AuthImage
              src={employee.avatar_url ?? employee.avatar}
              alt={employee.full_name ?? employee.name ?? ''}
              className="h-14 w-14 shrink-0 rounded-full object-cover ring-1 ring-gray-200"
            />
          ) : (
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand/50 text-xl font-bold text-brand/700 ring-1 ring-gray-200">
              {(employee.full_name ?? employee.name ?? '?').charAt(0)}
            </div>
          )}

          {/* Name & ID */}
          <div className="flex min-w-0 flex-1 flex-col">
            <TruncatedText
              as="h1"
              text={employee.full_name ?? employee.name ?? '—'}
              className="text-2xl font-bold text-gray-900"
            />
            <p className="text-sm text-gray-500 font-medium">{code ?? ''}</p>
          </div>
        </div>

        {/* Action Button — hidden entirely when the role can't perform any
            of the three workforce-context actions (Edit / Refresh / Revoke).
            All three share `workforce.edit` per the whitelist comment above. */}
        {canEditWorkforce && (
          <MyPopper
            placement="bottom-end"
            target={(open, show) => (
              <MyButton
                onClick={show}
                color="primary"
                variant="filled"
                size="md"
                customClassname="gap-2"
              >
                <Zap className="h-5 w-5" />
                Action
                <ChevronDown className={`h-5 w-5 transition-transform ${open ? 'rotate-180' : ''}`} />
              </MyButton>
            )}
          >
            {(_open, _anchorEl, _show, close) => {
              // Candidates carry one-time consent burned at CLIK completion —
              // nothing to revoke, so drop the entry entirely. For employees
              // the entry stays visible but is disabled unless consent is
              // currently "active" (the BE's computeConsentStatus returns the
              // same value used here, so server-side gating mirrors the UI).
              const isCandidate = String(employee.category ?? '').toLowerCase() === 'candidate'
              const canRevoke = !isCandidate && String(consentStatus ?? '').toLowerCase() === 'active'

              const buttonGroup = [
                {
                  icon: <Edit01 />,
                  label: 'Edit',
                  onClick: () => {
                    setIsEditOpen(true)
                    close()
                  },
                },
                {
                  icon: <Repeat04 />,
                  label: isRefreshing ? 'Refreshing…' : 'Refresh report',
                  disabled: isRefreshing,
                  onClick: () => handleRefreshReport(close),
                },
              ]

              if (!isCandidate) {
                buttonGroup.push({
                  icon: <LinkBroken02 />,
                  label: 'Revoke',
                  color: canRevoke ? '#b42318' : undefined,
                  disabled: !canRevoke,
                  onClick: () => {
                    if (!canRevoke) return
                    setIsRevokeModalOpen(true)
                    close()
                  },
                })
              }

              return <MyContextMenu menuButtonGroups={[buttonGroup]} />
            }}
          </MyPopper>
        )}
      </div>

      {/* Badges Section */}
      {(consentStatus || updatedLabel) && (
        <div className="flex items-center gap-2">
          {consentStatus && <MyConsentStatusChip status={consentStatus} />}
          {updatedLabel && (
            <MyChip
              label={updatedLabel}
              color="modern"
              variant="modern"
              size="sm"
              rounded="lg"
              startAdornment={<File06 className="h-3 w-3 text-brand/700" />}
            />
          )}
        </div>
      )}

      {/* Tabs & Filters Section */}
      <div className="flex items-center justify-between gap-4 mt-2">
        <MyHorizontalTabV2
          tabs={tabs}
          value={currentTabs.type}
          onChange={(val) =>
            setCurrentTabs({
              type: val,
            })
          }
          fitContent
        />

        {/* Enquiry switcher (date/report picker) and Filters button are hidden
            until their filtering behaviour is implemented. Re-enable once the
            date filter and Filters interaction actually work. */}
        {/* <div className="flex items-center gap-3">
          {currentTabs.type !== 'personal_information' && (
            <EnquirySwitcher enquiryHistory={employee?.enquiry_history} />
          )}

          <MyButton color="secondary" variant="outlined" size="md" customClassname="gap-2">
            <FilterLines className="h-5 w-5 text-gray-500" />
            Filters
          </MyButton>
        </div> */}
      </div>
      <MyStackedModalSlider
        open={isEditOpen}
        offset={0}
        zIndex={1000}
        element={
          <EditEmployeeSlider employee={employee} onClose={() => setIsEditOpen(false)} />
        }
        onClose={() => setIsEditOpen(false)}
        scrim
      />
      <RevokeReportForm
        open={isRevokeModalOpen}
        onClose={() => setIsRevokeModalOpen(false)}
        consentExpiry={employee.consent_expiry_at ?? employee.consentExpiryAt}
        onRevoke={async (payload) => {
          // Errors here bubble up to RevokeReportForm.onSubmit, which keeps
          // the modal open and toasts the failure — that way a failed revoke
          // never silently closes the modal as if it had succeeded.
          const enquiryId = resolveLatestEnquiryId()
          if (!enquiryId) {
            myToaster({ message: 'No enquiry to revoke.', type: 'error' })
            throw new Error('No enquiry to revoke')
          }
          const res = await ReportEnquiryService.revoke(enquiryId, payload)
          myToaster(res)
          // Refetch both the workforce detail (consent badge + history in the
          // open slide-in) AND the workforce LIST (so when the slide-in is
          // closed the row's consent chip is already flipped to Expired).
          // Awaited inside onRevoke so the modal stays open until both lands
          // — when the modal closes the user sees fresh data immediately.
          await Promise.all([refetchDetail(), getWorkforce()])
        }}
      />
    </div>
  )
}

ProfileHeader.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    full_name: PropTypes.string,
    name: PropTypes.string,
    code: PropTypes.string,
    employeeId: PropTypes.string,
    avatar_url: PropTypes.string,
    avatar: PropTypes.string,
    enquiry_history: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        reference_number: PropTypes.string,
        status: PropTypes.string,
      })
    ),
  }).isRequired,
}
